import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import InputField from '@/UIlibrary/InputField/InputField';
import Button from '@/UIlibrary/Button/Button';
import { Typography } from '@/UIlibrary/Typography/Typography';
import Logo from '@/stories/Components/Logo/Logo';
import { useTranslations } from 'use-intl';
import { useAuth } from '@/lib/auth';
import logger from '@/logger';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import { SignInMutation, useSignInMutation } from '@/graphql/__generated__/authentication';
import { useContactInformationQuery } from '@/graphql/__generated__/accounts';
import LocationRequest from './LocationRequest';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { BRAND_COLORS } from '@/UIlibrary/globalTheme';

type LoginProps = {
	message?: string | null;
	prefilledEmail?: string;
	showOnboardMessage?: boolean;
};

const Login: React.FC<LoginProps> = ({ message, prefilledEmail = '', showOnboardMessage }) => {
	const { lang } = useParams();
	const [searchParams] = useSearchParams();
	const expired = searchParams.get('expired');
	const emailFromQuery = searchParams.get('email');

	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		email: emailFromQuery || prefilledEmail,
		password: ''
	});
	const [coordinates, setCoordinates] = useState<{ latitude: number; longitude: number } | null>(
		null
	);
	const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
	const { setToken } = useAuth();
	const t = useTranslations('Login');
	const authenticationClient = useApolloClient(SCHEMAS.AUTHENTICATION);
	const accountsClient = useApolloClient(SCHEMAS.ACCOUNTS);

	// Create state to track if we should check contact info
	const [shouldCheckContactInfo, setShouldCheckContactInfo] = useState(false);
	const [loginSuccessful, setLoginSuccessful] = useState(false);

	// Add contact information query with proper handling
	const { loading: contactLoading } = useContactInformationQuery({
		client: accountsClient,
		skip: !shouldCheckContactInfo,
		fetchPolicy: 'network-only',
		onCompleted: (data) => {
			// If login was successful
			if (loginSuccessful) {
				if (data.myContactInformation) {
					// If email is not verified, show error toast and log the user out
					if (!data.myContactInformation.isEmailVerified) {
						toast.error(t('PleaseVerifyEmail'), { duration: 10000 });

						setToken(null);

						// Reset the states
						setLoginSuccessful(false);
						setShouldCheckContactInfo(false);
					} else {
						// If email is verified, proceed to the dashboard or redirect as needed
						logger.debug('Email verified, proceeding with login');
						navigate(`/${lang}/center/clients`);
					}
				}
			}
		},
		onError: (error) => {
			logger.error('Error fetching contact information:', error);
			toast.error(t('UnexpectedError'), { duration: 5000 });

			setToken(null);
			setLoginSuccessful(false);
			setShouldCheckContactInfo(false);
		}
	});

	const [signIn, { loading }] = useSignInMutation({
		client: authenticationClient,
		onCompleted: (data: SignInMutation) => {
			if (!data.signIn) return;

			if (data.signIn.successful && data.signIn.result?.jwt) {
				setToken(data.signIn.result.jwt);

				setLoginSuccessful(true);

				setShouldCheckContactInfo(true);
				return;
			}

			if (data.signIn.mutationErrors) {
				handleMutationErrors(data.signIn.mutationErrors);
			}
		},
		onError: (error) => {
			logger.error(error);
			toast.error(t('UnexpectedError'), { duration: 5000 });
		}
	});

	const handleMutationErrors = (
		errors: NonNullable<NonNullable<SignInMutation['signIn']>['mutationErrors']>
	) => {
		const newFieldErrors: { [key: string]: string } = {};

		errors.forEach((error) => {
			if (!error) return;

			switch (error.__typename) {
				case 'InvalidCredentials':
					toast.error(t('InvalidCredentials'), { duration: 5000 });
					break;
				case 'ActionFailed':
				case 'InvalidAction':
					toast.error(t('UnexpectedError'), { duration: 5000 });
					break;
				case 'InvalidInput':
					newFieldErrors[error.field] = error.message;
					break;
			}
		});

		setFieldErrors(newFieldErrors);
	};

	const handleLocationReceived = (location: { latitude: number; longitude: number }) => {
		setCoordinates(location);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setFieldErrors({});

		if (!formData.email.trim() || !formData.password.trim()) {
			setFieldErrors({ form: t('AllFieldsRequired') });
			return;
		}

		try {
			await signIn({
				variables: {
					input: {
						email: formData.email,
						password: formData.password,
						latitude: String(coordinates?.latitude ?? 0),
						longitude: String(coordinates?.longitude ?? 0)
					}
				}
			});
		} catch (err) {
			logger.error(err);
			toast.error(t('UnexpectedError'), { duration: 5000 });
		}
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value
		}));
		if (fieldErrors[name]) {
			setFieldErrors((prev) => {
				const updated = { ...prev };
				delete updated[name];
				return updated;
			});
		}
	};

	useEffect(() => {
		if (emailFromQuery && emailFromQuery !== formData.email) {
			setFormData((prev) => ({
				...prev,
				email: emailFromQuery
			}));
		}
	}, [emailFromQuery]);

	useEffect(() => {
		if (message) {
			toast.error(message, { duration: 15000 });
		}
	}, [message]);

	useEffect(() => {
		if (expired) {
			toast.error(t('ExpiredSession'), { duration: 15000 });
		}
	}, [expired, t]);

	useEffect(() => {
		if (showOnboardMessage) {
			toast.success(t('AccountMustBeApproved'), { duration: 15000 });
		}
	}, [showOnboardMessage, t]);

	return (
		<>
			<LocationRequest onLocationReceived={handleLocationReceived} />
			<div
				className="flex min-h-screen"
				style={{
					background: `linear-gradient(to right top, #4560FF, #3d54e6, #3548cc, #2d3cb3, #253099)`
				}}
			>
				<div className="w-full flex justify-center items-center">
					<form
						className="flex flex-col w-11/12 md:w-full max-w-[452px] mx-auto bg-white py-12 px-11 rounded-3xl"
						onSubmit={handleSubmit}
					>
						<div className="flex flex-row justify-center">
							<Logo src="/logo-white.jpg" width={300} height={250} />
						</div>
						<div className="flex flex-col gap-8">
							<Typography size="sm" type="display" weight="semibold">
								{t('Welcome')}
							</Typography>
							<div className="flex flex-col gap-2">
								<InputField
									name="email"
									label="Email"
									value={formData.email}
									onChange={handleChange}
									loading={loading || contactLoading}
									disabled={loading || contactLoading}
									placeholder="email@fincluyente.com"
									size="auto"
									error={!!fieldErrors.email}
									hint={fieldErrors.email || ''}
									focused={true}
								/>
								<div className="mb-3">
									<InputField
										name="password"
										type="password"
										label={t('Password')}
										value={formData.password}
										onChange={handleChange}
										loading={loading || contactLoading}
										disabled={loading || contactLoading}
										size="auto"
										placeholder={t('Password')}
										error={!!fieldErrors.password}
										hint={fieldErrors.password || ''}
									/>
									<div className="flex items-center justify-end">
										<Link to={`/${lang}/recover`}>
											<Typography size="xs" weight="semibold" color={BRAND_COLORS.brand700}>
												{t('ForgotPassword')}
											</Typography>
										</Link>
									</div>
								</div>
								<Button
									dataTestId="loginButton"
									disabled={loading || contactLoading}
									loading={loading || contactLoading}
									label={t('Login')}
									size="auto"
									type="submit"
								/>
							</div>
							<div className="flex flex-row gap-1 justify-end">
								<Typography color="subtitle" size="sm">
									{t('Powered')}
								</Typography>
								<Logo src="/orkestus-text-logo.svg" width={110} height={25} />
							</div>
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default Login;
